import { useEffect, useState } from "react";
import api from "@/lib/api";

const formatMiami = (iso, withTime = false) => {
  if (!iso) return "";
  try {
    // Date-only strings (YYYY-MM-DD) are rendered without timezone shift
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
    if (dateOnly && !withTime) {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("es-US", { day: "numeric", month: "long", year: "numeric" });
    }
    const opts = { day: "numeric", month: "long", year: "numeric", timeZone: "America/New_York" };
    if (withTime) {
      opts.hour = "numeric";
      opts.minute = "2-digit";
      opts.hour12 = true;
    }
    return new Date(iso).toLocaleString("es-US", opts) + (withTime ? " (Miami)" : "");
  } catch {
    return iso;
  }
};

export default function RetoReglas() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    document.title = "Reglas · El Reto Modo Pro";
    api.get("/reto/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) {
    return <main className="min-h-screen bg-mp-black pt-32 pb-24 text-mp-mute px-6">Cargando reglas…</main>;
  }

  return (
    <main data-testid="reto-reglas-page" className="min-h-screen bg-mp-black pt-32 md:pt-40 pb-24">
      <div className="mx-auto max-w-[900px] px-6 lg:px-12">
        <div className="mp-eyebrow">El Reto Modo Pro</div>
        <h1 className="mt-4 font-display font-bold text-white text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em]">
          Reglas oficiales del concurso
        </h1>

        <Section eyebrow="Organizador">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <KV label="Organizador y responsable legal" value={cfg.organizer_name} />
            <KV label="Marca" value={cfg.organizer_brand} />
            <KV label="Website" value={cfg.organizer_website} />
            <KV label="Instagram" value={cfg.organizer_instagram} />
            <KV label="Email" value={cfg.organizer_email} link={`mailto:${cfg.organizer_email}`} />
          </div>
          <p className="mt-6 text-white/70 text-sm leading-relaxed">
            {cfg.organizer_name} es responsable de la organización del concurso, la administración de
            participaciones, la selección de ganadores y la coordinación de la entrega de los premios.
          </p>
        </Section>

        <Section eyebrow="Fechas oficiales">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <KV label="Publicación del landing" value={formatMiami(cfg.publish_at)} />
            <KV label="Inicio de participaciones" value={formatMiami(cfg.start_at, true)} />
            <KV label="Cierre de participaciones" value={formatMiami(cfg.end_at, true)} />
            <KV label="Sorteo" value={cfg.draw_date} />
          </div>
          <p className="mt-4 text-mp-mute text-xs">Todas las fechas se refieren a la hora de Miami (Eastern Time).</p>
        </Section>

        <Section eyebrow="Participación">
          <ul className="list-disc pl-5 text-white/80 space-y-2 text-sm leading-relaxed">
            <li>Participación gratuita.</li>
            <li>Tres categorías de Palabras Pro — correspondientes a los 3 primeros episodios en YouTube.</li>
            <li>Debes completar al menos una Palabra Pro para participar.</li>
            <li>Selección aleatoria de ganadores entre quienes respondan correctamente en cada categoría.</li>
            <li>Una participación por persona (se identifica por email y teléfono).</li>
            <li>{cfg.eligibility}</li>
          </ul>
        </Section>

        <Section eyebrow="Premios">
          <ul className="space-y-4">
            {(cfg.prizes || []).map((p, i) => (
              <li key={i} className="border-l-2 border-mp-orange pl-5">
                <div className="font-display text-white text-xl">{p.title || `Premio #${i + 1}`}</div>
                <div className="text-white/70 text-sm mt-1 leading-relaxed">{p.description || ""}</div>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-white/70 text-sm leading-relaxed">
            Valor total objetivo actual: <span className="text-white">${cfg.prize_total_target} USD</span>.
            Valor máximo promocional: <span className="text-white">hasta ${cfg.prize_total_cap} USD</span>.
            Los valores anteriores son objetivos estimados de planificación y podrán incrementarse si se incorporan
            nuevos patrocinadores antes del sorteo. No se presentan como valores garantizados mientras las
            aportaciones no estén confirmadas.
          </p>
        </Section>

        <Section eyebrow="Composición de los premios">
          <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{cfg.prize_composition}</p>
        </Section>

        <Section eyebrow="Entrega">
          <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{cfg.delivery_info}</p>
        </Section>

        <Section eyebrow="Fotografías de ganadores">
          <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{cfg.photo_consent_info}</p>
        </Section>

        {cfg.sponsors?.length > 0 && (
          <Section eyebrow="Patrocinadores">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {cfg.sponsors.map((s, i) => (
                <li key={i} className="text-white/80">{s.name || s}</li>
              ))}
            </ul>
          </Section>
        )}

        {cfg.rules_text && (
          <Section eyebrow="Términos adicionales">
            <div className="text-white/80 leading-relaxed whitespace-pre-wrap text-sm">{cfg.rules_text}</div>
          </Section>
        )}

        <Section eyebrow="Aviso de privacidad">
          <p className="text-white/80 text-sm leading-relaxed">
            La información que compartes (nombre, email y teléfono) será utilizada exclusivamente por
            {" "}{cfg.organizer_name} para administrar el concurso, verificar las respuestas, contactar a los
            ganadores y coordinar la entrega de los premios. No compartimos tus datos con terceros con fines
            comerciales. Puedes solicitar la eliminación de tus datos escribiendo a{" "}
            <a className="text-mp-orange mp-link-underline" href={`mailto:${cfg.organizer_email}`}>{cfg.organizer_email}</a>.
          </p>
        </Section>

        <Section eyebrow="Notas">
          <ul className="list-disc pl-5 text-white/80 text-sm space-y-2 leading-relaxed">
            <li>Los premios serán en especie y no podrán canjearse por dinero en efectivo.</li>
            <li>Los ganadores serán anunciados en @modopropodcast.</li>
            <li>Esta promoción no está patrocinada, respaldada ni administrada por YouTube.</li>
            <li>Jupiter Productions LLC se reserva el derecho de descalificar participaciones duplicadas o fraudulentas.</li>
          </ul>
          <p className="mt-6 text-mp-orange font-semibold">Activa tu Modo Pro. 🔥</p>
        </Section>
      </div>
    </main>
  );
}

function Section({ eyebrow, children }) {
  return (
    <section className="mt-12 border-t border-white/10 pt-10">
      <div className="mp-eyebrow mb-4">{eyebrow}</div>
      {children}
    </section>
  );
}

function KV({ label, value, link }) {
  return (
    <div>
      <div className="mp-label">{label}</div>
      {link ? (
        <a href={link} className="text-mp-orange mp-link-underline">{value || "Por confirmar"}</a>
      ) : (
        <div className="font-display text-white text-lg">{value || "Por confirmar"}</div>
      )}
    </div>
  );
}
